import React, { useState } from 'react';
import { Dropdown, Menu, Button, Empty, Tooltip, Modal, Input, Typography } from 'antd';
import {
  StarOutlined,
  StarFilled,
  DeleteOutlined,
  PlusOutlined,
  HomeOutlined,
  ShopOutlined,
  SettingOutlined,
  RocketOutlined,
  LineChartOutlined,
  AimOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { 
  useDashboardNavigation, 
  DashboardFavorite, 
  DashboardLevel 
} from '../../contexts/DashboardNavigationContext';
import styles from './DashboardFavorites.module.css';

const { Text } = Typography;

// ============================================================================
// ICON MAPPING
// ============================================================================

const getLevelIcon = (level: DashboardLevel): React.ReactNode => {
  const iconMap: Record<DashboardLevel, React.ReactNode> = {
    'business': <HomeOutlined />,
    'product': <ShopOutlined />,
    'products': <ShopOutlined />,
    'operations': <SettingOutlined />,
    'development': <RocketOutlined />,
    'projections': <LineChartOutlined />,
    'marketfit': <AimOutlined />,
    'stakeholders': <TeamOutlined />,
    'shareholders': <TeamOutlined />,
    'employees': <TeamOutlined />,
    'community': <TeamOutlined />,
    'customers': <CustomerServiceOutlined />,
  };
  return iconMap[level] || <StarOutlined />;
};

// ============================================================================
// COMPONENT
// ============================================================================

interface DashboardFavoritesProps {
  className?: string;
}

export const DashboardFavorites: React.FC<DashboardFavoritesProps> = ({ className }) => {
  const { 
    favorites, 
    currentLocation, 
    isFavorite, 
    toggleFavorite, 
    removeFavorite,
    navigateTo,
  } = useDashboardNavigation();

  const [isOpen, setIsOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newFavoriteLabel, setNewFavoriteLabel] = useState('');

  const isCurrentFavorite = isFavorite(currentLocation);

  const handleToggleFavorite = () => {
    if (isCurrentFavorite) {
      toggleFavorite();
    } else {
      setIsAddModalOpen(true);
    }
  };

  const handleAddFavorite = () => {
    if (newFavoriteLabel.trim()) {
      toggleFavorite(newFavoriteLabel.trim());
      setNewFavoriteLabel('');
      setIsAddModalOpen(false);
    }
  };

  const handleNavigateToFavorite = (favorite: DashboardFavorite) => {
    navigateTo(favorite.location);
    setIsOpen(false);
  };

  const handleRemoveFavorite = (e: React.MouseEvent, favoriteId: string) => {
    e.stopPropagation();
    removeFavorite(favoriteId);
  };

  const dropdownContent = (
    <div className={styles.dropdownContent}>
      <div className={styles.header}>
        <Text className={styles.headerTitle}>
          <StarFilled className={styles.headerIcon} /> Favorites
        </Text>
        <Tooltip title={isCurrentFavorite ? 'Remove from favorites' : 'Add current page'}>
          <Button
            type="text"
            size="small"
            icon={isCurrentFavorite ? <StarFilled /> : <PlusOutlined />}
            onClick={handleToggleFavorite}
            className={`${styles.addButton} ${isCurrentFavorite ? styles.active : ''}`}
          />
        </Tooltip>
      </div>

      {favorites.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text className={styles.emptyText}>
              No favorites yet. Click the star to add the current page.
            </Text>
          }
          className={styles.empty}
        />
      ) : (
        <Menu
          className={styles.menu}
          items={favorites.map((favorite) => ({
            key: favorite.id,
            icon: getLevelIcon(favorite.location.level),
            label: (
              <div className={styles.menuItem}>
                <div className={styles.menuItemContent}>
                  <span className={styles.menuItemLabel}>{favorite.label}</span>
                  {favorite.location.productName && (
                    <Text type="secondary" className={styles.menuItemSubtitle}>
                      {favorite.location.productName}
                    </Text>
                  )}
                </div>
                <Tooltip title="Remove">
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => handleRemoveFavorite(e, favorite.id)}
                    className={styles.deleteButton}
                  />
                </Tooltip>
              </div>
            ),
            onClick: () => handleNavigateToFavorite(favorite),
          }))}
        />
      )}
    </div>
  );

  return (
    <>
      <Dropdown
        open={isOpen}
        onOpenChange={setIsOpen}
        popupRender={() => dropdownContent}
        trigger={['click']}
        placement="bottomRight"
        className={className}
      >
        <button
          className={`${styles.triggerButton} ${isCurrentFavorite ? styles.favorited : ''}`}
          aria-label="Favorites"
        >
          {isCurrentFavorite ? <StarFilled /> : <StarOutlined />}
          {favorites.length > 0 && (
            <span className={styles.badge}>{favorites.length}</span>
          )}
        </button>
      </Dropdown>

      {/* Add Favorite Modal */}
      <Modal
        title="Add to Favorites"
        open={isAddModalOpen}
        onCancel={() => {
          setIsAddModalOpen(false);
          setNewFavoriteLabel('');
        }}
        onOk={handleAddFavorite}
        okText="Add"
        okButtonProps={{ disabled: !newFavoriteLabel.trim() }}
        className={styles.modal}
      >
        <Input
          placeholder="Enter a name for this favorite..."
          value={newFavoriteLabel}
          onChange={(e) => setNewFavoriteLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddFavorite();
            }
          }}
          autoFocus
        />
      </Modal>
    </>
  );
};

export default DashboardFavorites;
